'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useTenant } from '@/lib/tenant-context';
import { consumerMobileNav } from '@/lib/consumer-navigation';

export function MobileNav() {
  const pathname = usePathname();
  const { isAuthenticated } = useAuth();
  const { isTenantDomain } = useTenant();
  const [mounted, setMounted] = useState(false);

  // Auth and tenant contexts are restored from browser storage/cookies. Keep
  // the first client render identical to SSR even when a streamed boundary
  // hydrates after those parent providers have already restored their state.
  useEffect(() => setMounted(true), []);

  // Build nav items based on auth state.
  // On tenant subdomains (career.bootminds.com etc.) we don't render
  // a Sign In tab — those pages are candidate-facing and login happens
  // inline during the apply flow. The recruiter team navigates to
  // /login directly when they need to sign in.
  const navItems = consumerMobileNav({
    isAuthenticated: mounted && isAuthenticated,
    isTenantDomain: mounted && isTenantDomain,
  });

  // Don't show on apply, auth, admin, signup, or request-access pages (admin
  // has its own AppSidebar; the signup/request-access funnel is standalone)
  if (
    pathname.startsWith('/apply') ||
    pathname.startsWith('/auth') ||
    pathname.startsWith('/admin') ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/request-access')
  ) {
    return null;
  }

  return (
    <nav aria-label="Mobile navigation" className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t z-50 h-[var(--mobile-nav-height)] pb-[env(safe-area-inset-bottom,0px)]">
      <div className="flex items-center justify-around py-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === item.href;
          // Disable prefetch on marketing/auth tabs (/, /auth/login) but keep
          // default prefetching for /me/* dashboards which authenticated users
          // tap constantly. Phase 1.4 perf benchmark 2026-05-21.
          const shouldPrefetch = item.href.startsWith('/me/');
          return (
            <Link
              key={item.href}
              href={item.href}
              prefetch={shouldPrefetch ? undefined : false}
              aria-current={isActive ? 'page' : undefined}
              className={`flex flex-col items-center gap-1 px-4 py-2 ${
                isActive ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
